<template>
	<div class="relative">
		<Transition name="fade" mode="out-in">
			<div v-if="!businessDetails.isDetailsSubmitted" class="w-full p-4">
				<h3 class="text-2xl">Get in touch</h3>
				<p class="mb-8">
					Just enter a few simple details and a carbar specialist will
					be in touch shortly to find the ideal solution for you.
				</p>
				<form @submit.prevent="onSubmit">
					<div class="space-y-2">
						<div class="flex flex-col gap-2 md:flex-row">
							<InputField
								v-model="firstname"
								label="First name"
								name="firstname"
								class="w-full"
								:error="errors.firstname"
							/>
							<InputField
								v-model="lastname"
								label="Last name"
								name="lastname"
								class="w-full"
								:error="errors.lastname"
							/>
						</div>
						<InputField
							v-model="email"
							label="Email"
							name="email"
							:error="errors.email"
						/>
						<InputField
							v-model="business"
							label="Company name (optional)"
							name="business_name"
							:error="errors.business"
						/>
						<div class="flex flex-col gap-2 md:flex-row">
							<InputField
								v-model="mobile_number"
								label="Phone number"
								name="mobile_number"
								class="w-full"
								:error="errors.mobile_number"
							/>
							<StatesSelectbox
								ref="stateSelectBoxRef"
								v-model="state"
								name="state"
								label="State"
								class="w-full"
								:error="errors.state"
							/>
						</div>
						<Transition name="alert">
							<AlertBox v-if="hasError">
								<AlertMessages :messages="hasError" />
							</AlertBox>
						</Transition>
						<CarbarButton
							:is-full-width="false"
							:disabled="!meta.valid || isSubmitting"
							:show-loader="isSubmitting"
							class="mt-4"
						>
							<span class="text-white">Submit</span>
						</CarbarButton>
					</div>
				</form>
				<div class="mt-4 text-sm">
					By clicking the "Submit" button you acknowledge that you
					have read and agree to abide by carbar's
					<CarbarLink
						font-size="text-sm"
						href="/privacy-policy"
						target="_blank"
					>
						<span>privacy policy</span>
					</CarbarLink>
					and
					<CarbarLink
						font-size="text-sm"
						href="/terms-and-conditions"
						target="_blank"
					>
						<span>terms and conditions</span>
					</CarbarLink>
					<span>*</span>
				</div>
			</div>
			<div
				v-else-if="businessDetails.isDetailsSubmitted"
				class="w-full p-4"
			>
				<h3 class="text-2xl">Thank you</h3>
				<p>
					We have received your interest and will be in touch shortly.
				</p>
				<CarbarButton
					:is-full-width="false"
					class="mt-8"
					@click="onClose"
				>
					<span class="text-white">Close</span>
				</CarbarButton>
			</div>
		</Transition>
	</div>
</template>

<script setup>
import { useForm, useField } from 'vee-validate'
import * as yup from 'yup'
import CarbarButton from '@/components/buttons/CarbarButton.vue'
import InputField from '@/components/inputs/InputField.vue'
import { useBusinessDetails } from '@/stores/get-in-touch'
import AlertBox from '@/components/alerts/AlertBox.vue'
import AlertMessages from '@/components/alerts/AlertMessages.vue'
import CarbarLink from '@/components/links/CarbarLink.vue'
import StatesSelectbox from '@/components/selectboxes/StatesSelectbox.vue'

const businessDetails = useBusinessDetails()
const hasError = computed(() => businessDetails.hasErrorsEnquireForm)

const { handleSubmit, errors, isSubmitting, meta, resetForm } = useForm({
	validationSchema: yup.object({
		firstname: yup.string().required('First name is required'),
		lastname: yup.string().required('Last name is required'),
		email: yup
			.string()
			.trim()
			.email('Enter valid email')
			.required('Email is required'),
		business: yup.string(),
		mobile_number: yup
			.string()
			.matches(
				// eslint-disable-next-line security/detect-unsafe-regex
				/^(?:\+?61|0)[23478](?:[ -]?[0-9]){8}$/,
				'Must be a number registered in Australia'
			)
			.required('Contact number is required'),
		state: yup.string().required('State is required'),
	}),
})

const { value: firstname } = useField('firstname')
const { value: lastname } = useField('lastname')
const { value: business } = useField('business')
const { value: email } = useField('email')
const { value: mobile_number } = useField('mobile_number')
const { value: state } = useField('state')

const updateFormData = (formData, values) => {
	const updatedFormData = formData
	updatedFormData.firstname = values.firstname
	updatedFormData.lastname = values.lastname
	updatedFormData.company = values.business
	updatedFormData.email = values.email
	updatedFormData.phone = values.mobile_number
	updatedFormData.state_dropdown_ = values.state

	return updatedFormData
}

const onSubmit = handleSubmit(async (values) => {
	businessDetails.details = updateFormData(businessDetails.details, values)
	await businessDetails.submitInterestForm()
})

const stateSelectBoxRef = ref(null)
const resetFormFields = () => {
	stateSelectBoxRef.value.reset()
	resetForm()
}
defineExpose({ resetFormFields })

const onClose = () => {
	emit('onClose')
}
const emit = defineEmits(['onClose'])
</script>
